"use client";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import { useLanguage } from "@/lib/i18n";

export default function PrivacyPolicyPage() {
  const { language } = useLanguage();

  const content = language === "id" ? {
    title: "Privacy Policy",
    subtitle: "Komitmen kami untuk melindungi privasi dan data pribadi Anda.",
    superText: "Legal Information",
    lastUpdated: "Terakhir Diperbarui: 29 Januari 2026",
    sections: [
      {
        title: "1. Pendahuluan",
        content: `Selamat datang di <strong class="text-brand-gold">KinKen Kost Elite</strong> ("kami", "milik kami", "KinKen"). Sebagai penyedia akomodasi premium di Bali, kami menghormati privasi Anda dan berkomitmen untuk melindunginya melalui kepatuhan terhadap kebijakan ini. Kebijakan ini menjelaskan jenis informasi yang mungkin kami kumpulkan dari Anda atau yang Anda berikan saat mengunjungi situs web kami atau melakukan reservasi.`
      },
      {
        title: "2. Informasi yang Kami Kumpulkan",
        intro: "Kami mengumpulkan beberapa jenis informasi untuk memberikan pengalaman premium yang dipersonalisasi:",
        list: [
          "<strong>Informasi Pribadi:</strong> Nama lengkap, alamat email, nomor telepon, alamat rumah, tanggal lahir, kebangsaan, dan detail paspor (untuk check-in).",
          "<strong>Informasi Pembayaran:</strong> Data kartu kredit/debit yang dienkripsi untuk keperluan transaksi reservasi dan deposit.",
          "<strong>Preferensi Pribadi:</strong> Preferensi kamar, permintaan khusus, makanan/minuman favorit untuk layanan concierge yang dipersonalisasi.",
          "<strong>Data Teknis:</strong> Alamat IP, jenis browser, perangkat, dan pola penggunaan website untuk optimasi pengalaman."
        ]
      },
      {
        title: "3. Bagaimana Kami Menggunakan Informasi Anda",
        intro: "Sebagai bagian dari layanan luxury hospitality kami, informasi digunakan untuk:",
        list: [
          "Proses reservasi, check-in, dan pembayaran yang smooth",
          "Layanan concierge yang dipersonalisasi sebelum dan selama tinggal",
          "Komunikasi eksklusif tentang promosi khusus untuk penghuni KinKen",
          "Peningkatan terus-menerus layanan dan fasilitas kami",
          "Kepatuhan terhadap peraturan pemerintah Indonesia (pelaporan tamu asing)"
        ]
      },
      {
        title: "4. Keamanan Data Premium",
        content: `Sebagai properti premium, kami menerapkan standar keamanan data tertinggi:<br/><br/>• <strong>Enkripsi SSL 256-bit</strong> untuk semua transaksi online<br/>• <strong>Sistem pemesanan terenkripsi</strong> dengan tokenisasi pembayaran<br/>• <strong>Access control ketat</strong> untuk data sensitif penghuni<br/>• <strong>Retensi data terbatas</strong> sesuai kebutuhan operasional<br/>• <strong>Audit berkala</strong> oleh pihak ketiga independen`
      },
      {
        title: "5. Data Sharing dengan Pihak Ketiga",
        content: `Kami hanya membagikan data dengan:<br/><br/>• <strong>Pihak Berwenang:</strong> Untuk kepatuhan hukum (imigrasi, pajak)<br/>• <strong>Mitra Premium:</strong> Restoran, spa, dan tour operator eksklusif hanya dengan persetujuan Anda untuk layanan concierge<br/>• <strong>Payment Processors:</strong> Gateway pembayaran yang bersertifikasi PCI DSS`
      },
      {
        title: "6. Hak Anda",
        content: `Anda memiliki hak untuk:<br/><br/>• Mengakses dan memperbaiki data pribadi Anda<br/>• Meminta penghapusan data (hak untuk dilupakan)<br/>• Menolak pemrosesan data untuk marketing<br/>• Meminta portabilitas data ke penyedia lain<br/>• Mengajukan keluhan ke otoritas perlindungan data`
      },
      {
        title: "7. Hubungi Kami",
        content: `Untuk pertanyaan tentang privasi atau untuk menjalankan hak Anda, hubungi:<br/><strong class="text-brand-gold">Data Protection Officer</strong><br/><strong class="text-brand-charcoal">privacy@kinkenelite.com</strong><br/><strong class="text-brand-charcoal">+62 361 123 4567</strong><br/><br/><em class="text-gray-400">Waktu respons: 2×24 jam kerja</em>`
      }
    ]
  } : {
    title: "Privacy Policy",
    subtitle: "Our commitment to protecting your privacy and personal data.",
    superText: "Legal Information",
    lastUpdated: "Last Updated: January 29, 2026",
    sections: [
      {
        title: "1. Introduction",
        content: `Welcome to <strong class="text-brand-gold">KinKen Kost Elite</strong> ("we", "our", "KinKen"). As a premium accommodation provider in Bali, we respect your privacy and are committed to protecting it through compliance with this policy. This policy explains the types of information we may collect from you or that you may provide when visiting our website or making reservations.`
      },
      {
        title: "2. Information We Collect",
        intro: "We collect several types of information to provide a personalized premium experience:",
        list: [
          "<strong>Personal Information:</strong> Full name, email address, phone number, home address, date of birth, nationality, and passport details (for check-in).",
          "<strong>Payment Information:</strong> Encrypted credit/debit card data for reservation and deposit transactions.",
          "<strong>Personal Preferences:</strong> Room preferences, special requests, favorite food/drinks for personalized concierge service.",
          "<strong>Technical Data:</strong> IP address, browser type, device, and website usage patterns for experience optimization."
        ]
      },
      {
        title: "3. How We Use Your Information",
        intro: "As part of our luxury hospitality service, information is used for:",
        list: [
          "Smooth reservation, check-in, and payment processes",
          "Personalized concierge service before and during your stay",
          "Exclusive communications about special promotions for KinKen residents",
          "Continuous improvement of our services and facilities",
          "Compliance with Indonesian government regulations (foreign guest reporting)"
        ]
      },
      {
        title: "4. Premium Data Security",
        content: `As a premium property, we implement the highest data security standards:<br/><br/>• <strong>256-bit SSL Encryption</strong> for all online transactions<br/>• <strong>Encrypted booking system</strong> with payment tokenization<br/>• <strong>Strict access control</strong> for sensitive resident data<br/>• <strong>Limited data retention</strong> according to operational needs<br/>• <strong>Regular audits</strong> by independent third parties`
      },
      {
        title: "5. Data Sharing with Third Parties",
        content: `We only share data with:<br/><br/>• <strong>Authorities:</strong> For legal compliance (immigration, taxes)<br/>• <strong>Premium Partners:</strong> Restaurants, spas, and exclusive tour operators only with your consent for concierge services<br/>• <strong>Payment Processors:</strong> PCI DSS certified payment gateways`
      },
      {
        title: "6. Your Rights",
        content: `You have the right to:<br/><br/>• Access and correct your personal data<br/>• Request data deletion (right to be forgotten)<br/>• Object to data processing for marketing<br/>• Request data portability to another provider<br/>• File a complaint with data protection authorities`
      },
      {
        title: "7. Contact Us",
        content: `For privacy questions or to exercise your rights, contact:<br/><strong class="text-brand-gold">Data Protection Officer</strong><br/><strong class="text-brand-charcoal">privacy@kinkenelite.com</strong><br/><strong class="text-brand-charcoal">+62 361 123 4567</strong><br/><br/><em class="text-gray-400">Response time: 2×24 business hours</em>`
      }
    ]
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={content.title}
        subtitle={content.subtitle}
        image="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070&auto=format&fit=crop"
        superText={content.superText}
      />

      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <div className="prose prose-lg prose-gray max-w-none">
            <p className="text-sm text-gray-400 mb-8 uppercase tracking-widest">
              {content.lastUpdated}
            </p>

            {content.sections.map((section, idx) => (
              <div key={idx}>
                <h3 className="font-serif text-2xl text-brand-charcoal mb-4 mt-8">
                  {section.title}
                </h3>
                
                {section.content && (
                  <p 
                    className="text-gray-500 font-light leading-relaxed mb-6"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                )}
                
                {section.intro && (
                  <p className="text-gray-500 font-light leading-relaxed mb-4">
                    {section.intro}
                  </p>
                )}
                
                {section.list && (
                  <ul className="list-disc pl-5 space-y-2 text-gray-500 font-light mb-6">
                    {section.list.map((item, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
